import { P } from "../../../components/Common/Typo/typo";
import bulb from "../../../assets/bulb.svg";

type CardProps = {
  title: string;
  value: number;
  unit: string;
  description: string;
  image?: string;
  chart?: boolean;
  estimate?: number;
};

export function Card({
  title,
  value,
  unit,
  description,
  image,
  estimate,
  prefix,
  suffix,
}: any) {
  return (
    <div
      className=" bg-white rounded-[10px] border border-[#d6d7df] w-full col-start-1 col-end-2 flex justify-between items-center relative"
      style={{ boxShadow: "0px 3px 10px 0px rgba(161, 183, 205, 1)" }}
    >
      <div className="p-2 flex flex-col justify-between">
        <P fontSize="20px" color="#1D1D1C">
          {title}
        </P>
        <div className="flex items-center gap-1">
          {prefix && (
            <p className="text-[20px] text-[#788796] mt-1">{prefix}</p>
          )}
          <P fontSize="30px" color="#212529" fontWeight="700">
            {value?.toLocaleString(undefined, {
              maximumFractionDigits: 2,
            }) ?? 0}
          </P>
          <p className="text-[20px] text-[#788796] mt-1">{unit}</p>
          {suffix}
        </div>
        {estimate && (
          <p className="text-[12px] text-[#788796] leading-[12px] -mt-2">
            {value > 100 ? "เพิ่มขึ้น" : "ลดลง"}{" "}
            {estimate?.toLocaleString(undefined, {
              maximumFractionDigits: 2,
            }) ?? 0}{" "}
            kgCO₂e
          </p>
        )}
        <P fontSize="15px" color="#212529">
          {description}
        </P>
      </div>
      <div
        className={`absolute bottom-0 right-0 bg-no-repeat bg-cover bg-center`}
      >
        <img src={image} alt="Icon" className={`w-full h-full object-cover`} />
      </div>
    </div>
  );
}

export function CardChart({
  title,
  value,
  unit,
  description,
  image,
  estimate,
  prefix,
  suffix,
}: any) {
  return (
    <div
      className={`bg-white rounded-[10px] border border-[#d6d7df] shadow-badgelogout w-full col-start-1 col-end-2 flex justify-between items-center z-1 ${
        image ? "relative" : ""
      }`}
    >
      {image && typeof image === "string" && (
        <img
          src={image}
          alt="Icon"
          className={`absolute top-0 left-0 w-full h-full object-cover rounded-[10px] z-0 opacity-80`}
        />
      )}
      <div className="p-2">
        <P fontSize="18px" color="#1D1D1C">
          {title}
        </P>
        <div className="flex items-center gap-1">
          {prefix && (
            <p className="text-[20px] text-[#788796] mt-1">{prefix}</p>
          )}
          <p className="text-[30px] text-[#212529] font-bold z-10">
            {value
              ? value?.toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })
              : 0}
          </p>
          <p className="text-[20px] text-[#788796] mt-1">{unit}</p>
          {suffix}
        </div>
        {estimate !== null && estimate !== undefined && (
          <p className="text-[12px] text-[#788796] leading-[12px] -mt-2">
            {value > 0 ? "ลดลง" : "เพิ่มขึ้น"}{" "}
            {Math.abs(estimate)?.toLocaleString(undefined, {
              maximumFractionDigits: 2,
            }) ?? 0}{" "}
            kgCO₂e
          </p>
        )}
        <P fontSize="16px" color="#212529">
          {description}
        </P>
      </div>
    </div>
  );
}

export function CardTip({ title, value, unit, listData, children }: any) {
  return (
    <div
      className="p-2 rounded-[10px] w-full col-start-1 col-end-2"
      style={{
        border: "1px rgba(30, 146, 130, 0.40) solid",
        background: "rgba(30, 146, 130, 0.20)",
        boxShadow: "0px 3px 8px 0px rgba(0, 0, 0, 0.08)",
        backdropFilter: "blur(4px)",
      }}
    >
      <div className="flex flex-col justify-between">
        <div className="flex items-start gap-[6px]">
          <img src={bulb} className="w-6 h-6" />
          <p className="text-[20px] text-[#1E9282] leading-[20px]">{title}</p>
        </div>

        <div className="flex items-center justify-between gap-[30px] mt-2">
          {value && (
            <p className="text-[18px] text-[#1E9282] leading-[20px]">{value}</p>
          )}
          {unit && (
            <p className="text-[18px] text-[#1E9282] leading-[20px]">{unit}</p>
          )}
          {listData && (
            <div>
              {listData.map((item: any, index: number) => {
                return (
                  <div className="px-2 leading-[20px] -my-0.5">
                    <p className="text-[18px] text-[#1E9282] leading-[20px]">
                      {index + 1}. {item}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}

export function SubCard({
  title,
  value,
  status,
  unit,
  isBold,
  iconCode,
  interval,
}: any) {
  const statusStyles: any = {
    ยอดเยี่ยม: "border-[#8BC6FF] bg-[#C3DFF8] text-[#065BA9]",
    ดี: "border-[#B3E49F] bg-[#E1FFD5] text-[#43A452]",
    ปานกลาง: "border-[#FFD337] bg-[#FFF6A1] text-[#F5A623]",
    "ไม่ดีต่อสุขภาพ(กลุ่มเสี่ยง)":
      "border-[#F9C36A] bg-[#FBDFB2] text-[#FF7A00]",
    ไม่ดีต่อสุขภาพ: "border-[#F7A19B] bg-[#FFD4D4] text-[#EF4337]",
  };
  const statusColor: any = {
    ยอดเยี่ยม: "#0E7EE4",
    ดี: "#43A452",
    ปานกลาง: "#F5A623",
    "ไม่ดีต่อสุขภาพ(กลุ่มเสี่ยง)": "#FF7A00",
    ไม่ดีต่อสุขภาพ: "#EF4337",
  };
  const statusBackground: any = {
    ยอดเยี่ยม: "#0E7EE440",
    ดี: "#A2E3A666",
    ปานกลาง: "#F3E48C66",
    "ไม่ดีต่อสุขภาพ(กลุ่มเสี่ยง)": "#F3B78C66",
    ไม่ดีต่อสุขภาพ: "#EA3F3F33",
  };
  return (
    <div
      className="p-[8px] rounded-[10px] border border-[#d6d7df] shadow-badgelogout w-full"
      style={{
        background:
          "linear-gradient(180deg, rgba(255, 255, 255, 0.80) 0%, #FFF 100%)",
        boxShadow: "0px 3px 10px 0px rgba(161, 183, 205, 1)",
      }}
    >
      <P
        fontSize={isBold ? "16px" : "20px"}
        color="#1D1D1C"
        fontWeight="700"
        className="truncate"
      >
        {title}
      </P>
      <div className="flex items-center justify-between gap-1 -mt-2">
        <div className="flex items-center gap-1">
          <P
            fontSize={isBold ? "32px" : "28px"}
            fontWeight={isBold ? "700" : "400"}
            color="#1D1D1C"
          >
            {value?.toLocaleString(undefined, {
              maximumFractionDigits: 2,
            })}
          </P>
          {unit && (
            <P
              fontSize={isBold ? "32px" : "24px"}
              fontWeight={isBold ? "700" : "400"}
              color="#1D1D1C"
            >
              {unit}
            </P>
          )}
        </div>
        {iconCode && (
          <img
            src={`/WeatherIcons/${iconCode}.png`}
            alt="weather icon"
            width={50}
            height={50}
          />
        )}
      </div>
      {status && (
        <div
          className={`px-[8px] rounded-md border text-center text-[20px] ${statusStyles[status]}`}
        >
          <p>
            {status === "ไม่ดีต่อสุขภาพ(กลุ่มเสี่ยง)"
              ? "ไม่ดีต่อกลุ่มเสี่ยง"
              : status}
          </p>
        </div>
      )}
      {!isBold && interval && (
        <div className="mt-[6px]">
          {Object.entries(interval).map(([key, value]: any) => {
            return (
              <div
                className="flex items-center justify-between text-[12px] my-[2px] px-[2px] py-[1px] rounded-[4px]"
                style={{ background: statusBackground[key] }}
              >
                <p className="text-[#1D1D1C] font-semibold">
                  {key === "ไม่ดีต่อสุขภาพ(กลุ่มเสี่ยง)"
                    ? "ไม่ดีต่อกลุ่มเสี่ยง"
                    : key}
                </p>
                <p
                  className="font-semibold"
                  style={{ color: statusColor[key] }}
                >{`${value[0]?.toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })} - ${value[1]?.toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })}`}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function EachFloorCard({ top, left, title, value, unit }: any) {
  return (
    <div
      className="absolute bg-white border border-[#EDEFF9] rounded-md w-[140px]"
      style={{
        top: `${top}%`,
        left: `${left}%`,
        padding: "4px 6px 2px 6px",
        boxShadow: "0px 3px 10px 0px rgba(161, 183, 205, 1)",
      }}
    >
      <P fontSize="24px" fontWeight="700" color="#063E40">
        {title}
      </P>
      <div className="flex items-center justify-start gap-[4px] h-[30px]">
        <P fontSize="30px" fontWeight="700" color="#EE7C20">
          {value?.toLocaleString(undefined, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          })}
        </P>
        <P fontSize="18px" fontWeight="700" color="#063E40">
          {unit}
        </P>
      </div>
    </div>
  );
}
